import { useFlags } from "launchdarkly-react-client-sdk";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { BOUNCED_EMAIL_HANDLING_FEATURE } from "constants/feature-flags";

import {
  buildBouncedEmailTooltip,
  useBouncedEmail
} from "./use-bounced-email.hook";

type WarningGlyphProps = {
  bouncedAt: string;
  detail?: string | null;
  sx?: SxProps;
};

function WarningGlyph({ bouncedAt, detail, sx }: WarningGlyphProps) {
  return (
    <Tooltip title={buildBouncedEmailTooltip(bouncedAt, detail)}>
      <ErrorOutlineIcon
        color="error"
        data-testid="bounced-email-warning-icon"
        sx={{ fontSize: 18, flexShrink: 0, ...sx }}
      />
    </Tooltip>
  );
}

type BouncedEmailWarningIconProps = {
  patientId?: number;
  sx?: SxProps;
};

/**
 * Compact sibling of BouncedEmailChip for dense surfaces (patient header,
 * contact modal, appointment form): a red warning glyph with the same tooltip.
 * Renders nothing unless the patient's email has a recorded permanent bounce.
 */
export default function BouncedEmailWarningIcon({
  patientId,
  sx
}: BouncedEmailWarningIconProps) {
  const { bouncedAt, detail } = useBouncedEmail(patientId);

  if (!bouncedAt) return null;

  return <WarningGlyph bouncedAt={bouncedAt} detail={detail} sx={sx} />;
}

type BouncedEmailWarningIconFromDataProps = {
  bouncedAt?: string | null;
  detail?: string | null;
  sx?: SxProps;
};

/**
 * Variant fed from data already on hand (a list/search projection carrying
 * email_bounced_at) instead of the per-patient fetch — for surfaces that
 * render many patients at once, where one query per row would fan out.
 * Still flag-gated like the fetching variant.
 */
export function BouncedEmailWarningIconFromData({
  bouncedAt,
  detail,
  sx
}: BouncedEmailWarningIconFromDataProps) {
  const flags = useFlags();

  if (!flags[BOUNCED_EMAIL_HANDLING_FEATURE] || !bouncedAt) return null;

  return <WarningGlyph bouncedAt={bouncedAt} detail={detail} sx={sx} />;
}
